import { useEffect, useMemo, useState } from "react";
import { useRoute } from "wouter";
import { Calendar, Clock, CheckCircle, XCircle, Loader2, ArrowLeft, ExternalLink, RefreshCw } from "lucide-react";

/**
 * /appointment/:token — the visitor's own booking, opened from the "Reschedule or cancel" link in their confirmation email.
 * Public (no login): the private token in the link is the key. Shows what and when (in the business's time zone), and lets the visitor
 * cancel or pick a new time from the open slots. Server: /api/public/appointments/:token (+ /slots, /cancel, /reschedule).
 */

interface AppointmentView {
  title: string;
  startAt: string;
  endAt: string;
  label: string;
  timezone: string;
  durationMins: number;
  status: string;
  kind: string;
  attendeeName: string | null;
  businessName: string;
  meetingLink: string | null;
  canChange: boolean;
  canReschedule: boolean;
  bookingUrl: string | null;
}

interface Slot { start: string; end: string; label?: string }

function fmtDay(iso: string, tz: string): string {
  try { return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: tz }); }
  catch { return new Date(iso).toLocaleDateString(); }
}
function fmtTime(iso: string, tz: string): string {
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }); }
  catch { return new Date(iso).toLocaleTimeString(); }
}
function zone(iso: string, tz: string): string {
  try { return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(iso)).find((p) => p.type === "timeZoneName")?.value || tz; }
  catch { return tz; }
}
/** YYYY-MM-DD of "today + i days" in the business's zone. */
function zonedDay(offset: number, tz: string): string {
  const d = new Date(Date.now() + offset * 86_400_000);
  try {
    const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    return p;
  } catch {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
}

export default function ManageAppointment() {
  const [, params] = useRoute("/appointment/:token");
  const token = params?.token || "";

  const [appt, setAppt] = useState<AppointmentView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const [picking, setPicking] = useState(false);
  const [day, setDay] = useState("");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [slotsKey, setSlotsKey] = useState(0);
  const [chosen, setChosen] = useState<Slot | null>(null);

  const load = () => {
    setLoading(true);
    setLoadError("");
    fetch(`/api/public/appointments/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.message || "We couldn't load this booking.");
        return body as AppointmentView;
      })
      .then((v) => setAppt(v))
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const tz = appt?.timezone || "America/Toronto";
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => zonedDay(i, tz)), [tz]);

  useEffect(() => {
    if (!picking || !day) return;
    setSlotsLoading(true);
    setSlotsError("");
    setSlots([]);
    fetch(`/api/public/appointments/${encodeURIComponent(token)}/slots?date=${day}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.message || "Unable to load open times");
        return body;
      })
      .then((b) => setSlots(b.slots || []))
      .catch((e) => setSlotsError(e.message))
      .finally(() => setSlotsLoading(false));
  }, [picking, day, token, slotsKey]);

  const cancel = async () => {
    setBusy(true);
    setActionError("");
    try {
      const r = await fetch(`/api/public/appointments/${encodeURIComponent(token)}/cancel`, { method: "POST", headers: { "Content-Type": "application/json" } });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.message || "We couldn't cancel this booking.");
      setAppt(body.appointment);
      setConfirmCancel(false);
      setNotice(body.already ? "This booking was already cancelled." : "Your booking is cancelled. We've let them know.");
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    if (!chosen) return;
    setBusy(true);
    setActionError("");
    try {
      const r = await fetch(`/api/public/appointments/${encodeURIComponent(token)}/reschedule`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ slotStart: chosen.start }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 409) { setActionError(body.message || "That time is no longer open. Please pick another."); setChosen(null); setSlotsKey((k) => k + 1); return; }
      if (!r.ok) throw new Error(body.message || "We couldn't change this booking.");
      setAppt(body.appointment);
      setPicking(false);
      setChosen(null);
      setNotice(body.message || "Your new time is booked.");
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center" data-testid="manage-loading">
        <Loader2 size={24} className="animate-spin text-blue-500" />
      </div>
    );
  }
  if (loadError || !appt) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4" data-testid="manage-error">
        <div className="max-w-md w-full text-center space-y-4">
          <XCircle size={32} className="text-red-400 mx-auto" />
          <p className="text-sm text-foreground/70">{loadError || "We couldn't find that booking."}</p>
          <button onClick={load} className="text-xs text-blue-400 hover:text-blue-300" data-testid="button-retry-load">Try again</button>
        </div>
      </div>
    );
  }

  const cancelled = appt.status === "CANCELLED";
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="manage-appointment-page">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center space-y-2">
            <p className="text-xs text-blue-400 uppercase tracking-[0.15em] font-bold" data-testid="text-business-name">{appt.businessName}</p>
            <h1 className="text-2xl font-bold" data-testid="text-manage-heading">
              {cancelled ? "Booking cancelled" : picking ? "Pick a new time" : "Your booking"}
            </h1>
            {appt.attendeeName && !picking && <p className="text-sm text-foreground/50">Hi {appt.attendeeName}</p>}
          </div>

          {notice && (
            <div className="px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-400 flex items-start gap-2" data-testid="text-manage-notice">
              <CheckCircle size={16} className="mt-0.5 shrink-0" />
              <span>{notice}</span>
            </div>
          )}
          {actionError && (
            <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400" data-testid="text-manage-error">{actionError}</div>
          )}

          <div className={`bg-foreground/[0.03] border border-foreground/[0.08] rounded-2xl p-6 space-y-4 ${cancelled ? "opacity-60" : ""}`}>
            <div className="flex items-start gap-3">
              <Calendar size={16} className="text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-foreground/30 uppercase tracking-wider font-bold mb-1">{appt.title}</p>
                <p className={`text-sm text-foreground ${cancelled ? "line-through" : ""}`} data-testid="text-appointment-day">{fmtDay(appt.startAt, tz)}</p>
                <p className="text-sm text-foreground/60" data-testid="text-appointment-time">
                  {fmtTime(appt.startAt, tz)} - {fmtTime(appt.endAt, tz)} {zone(appt.startAt, tz)}
                </p>
              </div>
            </div>
            {appt.meetingLink && !cancelled && (
              <div className="flex items-start gap-3">
                <ExternalLink size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <a href={appt.meetingLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:text-blue-300 underline break-all" data-testid="link-meeting">
                  Join the meeting
                </a>
              </div>
            )}
          </div>

          {cancelled ? (
            appt.bookingUrl ? (
              <a href={appt.bookingUrl} className="block text-center text-sm text-blue-400 hover:text-blue-300 underline" data-testid="link-book-again">Book a new time</a>
            ) : null
          ) : !appt.canChange ? (
            <p className="text-sm text-foreground/50 text-center" data-testid="text-cannot-change">
              This booking can't be changed online any more. Please reply to your confirmation email.
            </p>
          ) : picking ? (
            <div className="space-y-4" data-testid="reschedule-picker">
              <div className="grid grid-cols-7 gap-2">
                {days.map((d) => {
                  const dt = new Date(`${d}T12:00:00Z`);
                  const sel = d === day;
                  return (
                    <button
                      key={d}
                      onClick={() => { setDay(d); setChosen(null); setActionError(""); }}
                      className={`flex flex-col items-center py-2 rounded-xl text-center transition-all ${sel ? "bg-blue-600 text-foreground" : "bg-foreground/[0.03] border border-foreground/[0.06] text-foreground/50 hover:text-foreground/80"}`}
                      data-testid={`date-${d}`}
                    >
                      <span className="text-[10px] uppercase tracking-wider opacity-70">{dt.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}</span>
                      <span className="text-base font-bold">{dt.getUTCDate()}</span>
                    </button>
                  );
                })}
              </div>
              {!day ? (
                <p className="text-sm text-foreground/40 text-center">Choose a day.</p>
              ) : slotsLoading ? (
                <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-blue-500" /></div>
              ) : slotsError ? (
                <div className="text-center space-y-2">
                  <p className="text-sm text-red-400">{slotsError}</p>
                  <button onClick={() => setSlotsKey((k) => k + 1)} className="text-xs text-blue-400" data-testid="button-retry-slots">Try again</button>
                </div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-foreground/40 text-center" data-testid="text-no-slots">No open times that day. Try another day.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((s, i) => (
                    <button
                      key={s.start}
                      onClick={() => setChosen(s)}
                      className={`px-3 py-2 rounded-xl text-sm border transition-all ${chosen?.start === s.start ? "bg-blue-600 border-blue-500 text-foreground" : "bg-foreground/[0.03] border-foreground/[0.08] text-foreground/70 hover:border-blue-500/40"}`}
                      data-testid={`slot-${i}`}
                    >
                      {fmtTime(s.start, tz)}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-foreground/30 text-center">Times are in {zone(appt.startAt, tz)} ({tz})</p>
              <div className="flex gap-2">
                <button onClick={() => { setPicking(false); setChosen(null); setActionError(""); }} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm border border-foreground/[0.1] text-foreground/60" data-testid="button-back">
                  <ArrowLeft size={14} /> Back
                </button>
                <button onClick={reschedule} disabled={!chosen || busy} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-foreground" data-testid="button-confirm-reschedule">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />} {chosen ? `Move to ${fmtTime(chosen.start, tz)}` : "Choose a time"}
                </button>
              </div>
            </div>
          ) : confirmCancel ? (
            <div className="space-y-3" data-testid="cancel-confirm">
              <p className="text-sm text-foreground/70 text-center">Cancel this booking? {appt.businessName} will be told.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmCancel(false)} className="flex-1 px-4 py-3 rounded-xl text-sm border border-foreground/[0.1] text-foreground/60" data-testid="button-keep">Keep it</button>
                <button onClick={cancel} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white" data-testid="button-confirm-cancel">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Yes, cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {appt.canReschedule && (
                <button onClick={() => { setPicking(true); setDay(days[0]); setNotice(""); setActionError(""); }} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-500 text-foreground" data-testid="button-reschedule">
                  <RefreshCw size={14} /> Pick a new time
                </button>
              )}
              <button onClick={() => { setConfirmCancel(true); setNotice(""); setActionError(""); }} className="w-full px-4 py-3 rounded-xl text-sm border border-foreground/[0.1] text-foreground/70 hover:text-foreground" data-testid="button-cancel">
                Cancel booking
              </button>
            </div>
          )}
        </div>
      </div>
      <footer className="py-6 text-center text-xs text-foreground/20">Powered by Drpr.ai</footer>
    </div>
  );
}
